import React, { useCallback, useRef, useState } from "react";
import {
  Paper,
  IconButton,
  Tooltip,
  Typography,
  Box,
  CircularProgress,
  Chip,
  Divider,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import {
  DndContext,
  closestCenter,
  useSensor,
  useSensors,
  PointerSensor,
  TouchSensor,
  MouseSensor,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import {
  restrictToFirstScrollableAncestor,
  restrictToParentElement,
  restrictToVerticalAxis,
  restrictToWindowEdges,
} from "@dnd-kit/modifiers";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { useUpdateSingleNoteOrder } from "../../../../hooks/panelClient/useUpdateSingleNoteOrder";
import { setNoteViewed } from "../../../../hooks/panelClient/useSetNoteViewed";
import { DeleteNote } from "./DeleteNote";
import RemoveCircleIcon from "@mui/icons-material/RemoveCircle";
import { ModalDeleteWitness } from "./ModalDeleteWitness";
import EditSynthesis from "./EditSynthesis";
import { highlightText } from "../panelNotes/HighlightText";

const NotesTable = ({
  filteredData,
  token,
  refreshNotes,
  searchTerm,
  setItems,
}) => {
  const { updateSingleNoteOrder, isLoading } = useUpdateSingleNoteOrder(token);

  const handleSetVisto = useCallback(
    (id, viewed) => {
      setItems((prev) =>
        prev.map((item) =>
          item.id_sintesis === id ? { ...item, viewed } : item
        )
      );

      setNoteViewed(id, viewed, token);
    },
    [token, setItems]
  );

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    setItems((currentItems) => {
      const oldIndex = currentItems.findIndex(
        (item) => item.id_sintesis === active.id
      );
      const newIndex = currentItems.findIndex(
        (item) => item.id_sintesis === over.id
      );
      const newOrder = arrayMove(currentItems, oldIndex, newIndex).map(
        (item, index) => ({
          ...item,
          orden_editor: index,
        })
      );

      const movedItem = newOrder[newIndex];
      updateSingleNoteOrder(movedItem.id_sintesis, movedItem.orden_editor);
      newOrder[newIndex].position_changed = true;

      return newOrder;
    });
  };

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5,
      },
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 250,
        tolerance: 5,
      },
    }),
    useSensor(MouseSensor)
  );

  return (
    <>
      {isLoading && (
        <Box display="flex" justifyContent="center" my={2}>
          <CircularProgress />
        </Box>
      )}

      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
        modifiers={[
          restrictToVerticalAxis,
          restrictToWindowEdges,
          restrictToFirstScrollableAncestor,
          restrictToParentElement,
        ]}
      >
        <SortableContext
          items={filteredData.map((item) => item.id_sintesis)}
          strategy={verticalListSortingStrategy}
        >
          <Box
            sx={{
              "& > div": {
                margin: "20px 0",
              },
            }}
          >
            {filteredData.map((item) => (
              <SortableItem
                key={item.id_sintesis}
                item={item}
                onSetVisto={handleSetVisto}
                token={token}
                fetchNotasManualmente={refreshNotes}
                searchTerm={searchTerm}
              />
            ))}
          </Box>
        </SortableContext>
      </DndContext>
    </>
  );
};

const SortableItem = ({
  item,
  onSetVisto,
  token,
  fetchNotasManualmente,
  searchTerm,
}) => {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id: item.id_sintesis });
  const [expanded, setExpanded] = useState(false);
  const noteRef = useRef(null);
  const [selectedNote, setSelectedNote] = useState(null);
  const [modalDelete, setModalDelete] = useState(false);
  const [modalDeleteWitness, setModalDeleteWitness] = useState(false);
  const [witnessToDelete, setWitnessToDelete] = useState(null);
  const [deletedWitnesses, setDeletedWitnesses] = useState([]);
  const [modalEditSynthesis, setModalEditSynthesis] = useState(false);

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    cursor: "default",
    opacity: transform ? 0.5 : 1,
  };

  const handleExpand = () => {
    if (!item.viewed) onSetVisto(item.id_sintesis, true);
    setExpanded((prev) => !prev);
  };

  const handleClickTestigo = () => {
    if (!item.viewed) onSetVisto(item.id_sintesis, true);
  };

  const confirmDeleteWitness = (event, testigo) => {
    event.preventDefault();
    event.stopPropagation();
    setWitnessToDelete(testigo);
    setSelectedNote(item);
    setModalDeleteWitness(true);
  };

  const handleWitnessDeleted = (id_testigo) => {
    setDeletedWitnesses((prev) => [...prev, id_testigo]);
    fetchNotasManualmente();
  };

  return (
    <div
      ref={(el) => {
        setNodeRef(el);
        noteRef.current = el;
      }}
      style={style}
    >
      <Paper
        sx={{
          padding: 2,
          borderLeft: `6px solid ${
            item.position_changed ? "#fdd835" : "#9e9e9e"
          }`,
          borderTop: `4px solid ${item.viewed ? "#43a047" : "#e53935"}`,
          borderBottom: `4px solid ${item.viewed ? "#43a047" : "#e53935"}`,
          borderRight: `4px solid ${item.viewed ? "#43a047" : "#e53935"}`,
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Box
              {...attributes}
              {...listeners}
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                p: 0.5,
                borderRadius: 1,
                cursor: "grab",
                touchAction: "none",
              }}
            >
              <DragIndicatorIcon sx={{ color: "gray", fontSize: 26 }} />
            </Box>
            <Typography fontWeight="bold">
              {" "}
              {highlightText(item.titulo, searchTerm)}
            </Typography>
          </Box>
          <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
            <Tooltip
              title={item.viewed ? "Marcar como no visto" : "Marcar como visto"}
            >
              <IconButton
                onClick={() => onSetVisto(item.id_sintesis, !item.viewed)}
                size="small"
              >
                {item.viewed ? (
                  <VisibilityIcon sx={{ color: "green" }} />
                ) : (
                  <VisibilityOffIcon sx={{ color: "gray" }} />
                )}
              </IconButton>
            </Tooltip>
            <Tooltip title="Editar síntesis">
              <IconButton
                size="small"
                onClick={() => {
                  setSelectedNote(item);
                  setModalEditSynthesis(true);
                }}
              >
                <EditIcon sx={{ color: "#235789", fontSize: "20px" }} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Eliminar nota">
              <IconButton
                size="small"
                onClick={() => {
                  setSelectedNote(item);
                  setModalDelete(true);
                }}
              >
                <DeleteIcon sx={{ color: "red", fontSize: "20px" }} />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        <Typography
          sx={{
            mb: 1,
            textAlign: "justify",
            overflow: "hidden",
            whiteSpace: expanded ? "normal" : "nowrap",
            textOverflow: "ellipsis",
          }}
        >
          {highlightText(item.sintesis, searchTerm)}
        </Typography>

        {item.sintesis.length > 0 && (
          <Typography
            variant="body2"
            sx={{
              cursor: "pointer",
              color: "primary.main",
              mb: 2,
              width: "fit-content",
              "&:hover": { textDecoration: "underline" },
            }}
            onClick={handleExpand}
          >
            {expanded ? "Ver menos..." : "Ver más..."}
          </Typography>
        )}

        <Divider sx={{ my: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Testigos
          </Typography>
        </Divider>

        <div style={{ display: "flex", flexWrap: "wrap" }}>
          {(item?.testigo || [])
            .filter((t) => !deletedWitnesses.includes(t.witness_id))
            .map((testigo) => (
              <Chip
                key={testigo.witness_id}
                component="a"
                label={testigo.medio}
                href={testigo.url}
                target="_blank"
                rel="noopener noreferrer"
                clickable
                onClick={handleClickTestigo}
                onDelete={(e) => confirmDeleteWitness(e, testigo)}
                deleteIcon={<RemoveCircleIcon />}
                sx={{
                  marginRight: 1,
                  marginBottom: 1,
                  "&:hover": {
                    textDecoration: "underline",
                    color: "primary.main",
                  },
                }}
              />
            ))}
        </div>
      </Paper>

      <EditSynthesis
        token={token}
        open={modalEditSynthesis}
        close={() => setModalEditSynthesis(false)}
        note={selectedNote}
        onSaveSuccess={fetchNotasManualmente}
      />

      <DeleteNote
        token={token}
        open={modalDelete}
        close={() => setModalDelete(false)}
        note={selectedNote}
        onDeleteSuccess={fetchNotasManualmente}
      />

      <ModalDeleteWitness
        token={token}
        open={modalDeleteWitness}
        close={() => setModalDeleteWitness(false)}
        note={selectedNote}
        testigo={witnessToDelete}
        onWitnessDeleted={handleWitnessDeleted}
      />
    </div>
  );
};

export default NotesTable;
